import { useState } from "react";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { OrganizationAvatar } from "@fp/ui/components/base/avatar/organization-avatar";
import {
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useUserContext } from "@/components/user-context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Edit05 } from "@untitledui/icons";
import axios, { getErrorMessage } from "@/lib/axios";
import { toast } from "@/lib/toast";
import type { FormerOrganizationDto } from "@fp/admin-api-client";
import { AdminPageHeader } from "@/components/admin-page-header";
import { AdminLayout } from "@/components/admin-layout";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";

interface PendingJoinRequest {
  id: string;
  organizationId: string;
  organizationName: string;
  createdDate: string;
}

export const Route = createFileRoute("/organizations/")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { organizations, orgId, setOrgId, refetchOrganizations, claims } =
    useUserContext();

  const [changingOrgId, setChangingOrgId] = useState<string | null>(null);
  const [withdrawingRequestId, setWithdrawingRequestId] = useState<string | null>(
    null,
  );
  const [requestingJoinOrgId, setRequestingJoinOrgId] = useState<string | null>(
    null,
  );
  const [restoringOrgId, setRestoringOrgId] = useState<string | null>(null);
  const [acceptingInvitationId, setAcceptingInvitationId] = useState<string | null>(null);
  const [withdrawRequestModal, setWithdrawRequestModal] = useState<{ id: string; orgName: string } | null>(null);

  const { data: pendingRequests } = useQuery({
    queryKey: ["pendingJoinRequests"],
    queryFn: async () => {
      const response = await axios.get<PendingJoinRequest[]>(
        "/api/join-requests/pending",
      );
      return response.data;
    },
  });

  const { data: formerOrganizations } = useQuery({
    queryKey: ["formerOrganizations"],
    queryFn: async () => {
      const response = await axios.get<FormerOrganizationDto[]>(
        "/api/organizations/former",
      );
      return response.data;
    },
  });

  const withdrawRequestMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`/api/join-requests/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pendingJoinRequests"] });
      setWithdrawingRequestId(null);
    },
    onError: (error) => {
      setWithdrawingRequestId(null);
      toast.error(getErrorMessage(error, "Failed to withdraw request. Please try again."));
    },
  });

  const joinRequestMutation = useMutation({
    mutationFn: async (organizationId: string) => {
      await axios.post("/api/join-requests", {
        organizationId,
        firstName: claims?.firstName,
        lastName: claims?.lastName,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pendingJoinRequests"] });
      queryClient.invalidateQueries({ queryKey: ["formerOrganizations"] });
      setRequestingJoinOrgId(null);
    },
    onError: (error) => {
      setRequestingJoinOrgId(null);
      toast.error(getErrorMessage(error, "Failed to send join request. Please try again."));
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (organizationId: string) => {
      const response = await axios.post<{ organizationId: string }>(
        `/api/organizations/${organizationId}/restore`,
      );
      return response.data;
    },
    onSuccess: async (result) => {
      await refetchOrganizations();
      await setOrgId(result.organizationId);
      setRestoringOrgId(null);
    },
    onError: (error) => {
      setRestoringOrgId(null);
      toast.error(getErrorMessage(error, "Failed to restore organization. Please try again."));
    },
  });

  const acceptInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      await axios.post(`/api/received-invitations/${invitationId}/accept`);
    },
    onSuccess: async () => {
      await refetchOrganizations();
      queryClient.invalidateQueries({ queryKey: ["formerOrganizations"] });
      queryClient.invalidateQueries({ queryKey: ["received-invitations"] });
      setAcceptingInvitationId(null);
    },
    onError: (error) => {
      setAcceptingInvitationId(null);
      toast.error(getErrorMessage(error, "Failed to accept invitation. Please try again."));
    },
  });

  const handleSwitchOrg = async (newOrgId: string) => {
    if (newOrgId === orgId) return;
    setChangingOrgId(newOrgId);
    try {
      await setOrgId(newOrgId);
    } finally {
      setChangingOrgId(null);
    }
  };

  const handleWithdrawRequest = (id: string, orgName: string) => {
    setWithdrawRequestModal({ id, orgName });
  };

  const handleWithdrawRequestConfirm = () => {
    if (withdrawRequestModal) {
      setWithdrawingRequestId(withdrawRequestModal.id);
      withdrawRequestMutation.mutate(withdrawRequestModal.id);
      setWithdrawRequestModal(null);
    }
  };

  const handleRequestToJoin = (organizationId: string) => {
    setRequestingJoinOrgId(organizationId);
    joinRequestMutation.mutate(organizationId);
  };

  const handleRestore = (organizationId: string) => {
    setRestoringOrgId(organizationId);
    restoreMutation.mutate(organizationId);
  };

  const handleAcceptInvitation = (invitationId: string) => {
    setAcceptingInvitationId(invitationId);
    acceptInvitationMutation.mutate(invitationId);
  };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Organizations"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer>
        <div className="space-y-3">
          {[...organizations]
            .sort((a, b) =>
              a.organizationName.localeCompare(b.organizationName),
            )
            .map((org) => {
              const isCurrent = org.organizationId === orgId;
              const isChanging = changingOrgId === org.organizationId;

              return (
                <div
                  key={org.organizationId}
                  className="p-4 bg-white border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-4 min-w-0">
                      <OrganizationAvatar
                        name={org.organizationName}
                        logoUrl={org.logoUrl}
                        size="lg"
                        className="shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">
                            {org.organizationName}
                          </span>
                          {isCurrent && organizations.length > 1 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 shrink-0">
                              Current
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0">
                      {isCurrent ? (
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={() => navigate({ to: "/organizations/edit" })}
                          iconLeading={Edit05}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleSwitchOrg(org.organizationId)}
                          color="secondary"
                          isDisabled={isChanging || !!changingOrgId}
                        >
                          {isChanging ? "Switching..." : "Switch"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

          {pendingRequests?.map((request) => (
            <div
              key={request.id}
              className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {request.organizationName}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                    Request Pending
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Waiting for approval since {formatLocalDate(request.createdDate, "dateWithYear")}
                </div>
              </div>
              <Button
                onClick={() => handleWithdrawRequest(request.id, request.organizationName)}
                color="secondary"
                isDisabled={withdrawingRequestId === request.id}
              >
                {withdrawingRequestId === request.id
                  ? "Withdrawing..."
                  : "Withdraw"}
              </Button>
            </div>
          ))}

          {formerOrganizations && formerOrganizations.length > 0 && (
            <>
              <div className="pt-4 pb-2">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Former Organizations
                </h3>
              </div>
              {formerOrganizations
                .filter(
                  (org) =>
                    org.organizationIsActive || org.roleCode === "ORG_OWNER",
                )
                .map((org) => {
                  const isOwner = org.roleCode === "ORG_OWNER";
                  const isRequesting =
                    requestingJoinOrgId === org.organizationId;
                  const isRestoring = restoringOrgId === org.organizationId;
                  const hasPendingRequest = pendingRequests?.some(
                    (req) => req.organizationId === org.organizationId,
                  );
                  const pendingInvitations = org.pendingInvitations;
                  const hasPendingInvitation = pendingInvitations.length > 0;
                  const primaryInvitation = pendingInvitations[0];
                  const isAccepting = primaryInvitation && acceptingInvitationId === primaryInvitation.id;
                  const hasMultipleInvitations = pendingInvitations.length > 1;

                  return (
                    <div
                      key={org.organizationId}
                      className="p-4 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-4 min-w-0">
                          <OrganizationAvatar
                            name={org.organizationName}
                            logoUrl={org.logoUrl}
                            size="lg"
                            className="shrink-0"
                          />
                          <div className="min-w-0">
                            <span className="font-medium truncate">
                              {org.organizationName}
                            </span>
                            {!hasPendingInvitation && (
                              <div className="text-sm text-gray-500">
                                Left {formatLocalDate(org.leaveDate, "dateWithYear")}
                              </div>
                            )}
                            {hasPendingInvitation && (
                              <div className="text-xs text-green-600 mt-1">
                                {hasMultipleInvitations
                                  ? `${pendingInvitations.length} invitations pending`
                                  : `Invited as ${primaryInvitation.invitationType.toLowerCase()}`}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="shrink-0">
                          {isOwner ? (
                            <Button
                              onClick={() => handleRestore(org.organizationId)}
                              color="secondary"
                              size="sm"
                              isDisabled={isRestoring}
                            >
                              {isRestoring ? "Restoring..." : "Restore"}
                            </Button>
                          ) : hasPendingInvitation ? (
                            <Button
                              onClick={() => handleAcceptInvitation(primaryInvitation.id)}
                              color="primary"
                              size="sm"
                              isDisabled={isAccepting}
                            >
                              {isAccepting ? "Accepting..." : "Accept Invite"}
                            </Button>
                          ) : hasPendingRequest ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              Request Pending
                            </span>
                          ) : (
                            <Button
                              onClick={() => handleRequestToJoin(org.organizationId)}
                              color="secondary"
                              size="sm"
                              isDisabled={isRequesting}
                            >
                              {isRequesting
                                ? "Requesting..."
                                : "Request to Join"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </>
          )}

          {organizations.length === 0 &&
          (!pendingRequests || pendingRequests.length === 0) ? (
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <p className="text-gray-500 mb-3">You are not a member of any organizations yet.</p>
              <Button onClick={() => navigate({ to: "/onboarding", search: { add: false } })} color="primary">
                Create or Join an Organization
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => navigate({ to: "/onboarding", search: { add: true } })}
              color="secondary"
              className="mt-2"
            >
              Create or Join Another Organization
            </Button>
          )}
        </div>
      </PageContentContainer>
      <ConfirmationModal
        isOpen={!!withdrawRequestModal}
        onClose={() => setWithdrawRequestModal(null)}
        onConfirm={handleWithdrawRequestConfirm}
        title="Withdraw Join Request"
        message={`Are you sure you want to withdraw your request to join ${withdrawRequestModal?.orgName}?`}
        confirmText="Withdraw Request"
        cancelText="Keep Request"
        isLoading={!!withdrawingRequestId}
      />
    </AdminLayout>
  );
}
